import { apiRequest } from "./queryClient";

// Currency type definition
export type Currency = {
  id: number;
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
  displayOrder: number;
};

// Default symbols for common currencies
const CURRENCY_SYMBOLS: Record<string, string> = {
  "USD": "$",
  "EUR": "€",
  "INR": "₹",
  "GBP": "£",
  "JPY": "¥",
  "AUD": "A$",
  "CAD": "C$",
  "CHF": "CHF",
  "CNY": "¥",
  // Add more as needed
};

// Fallback list of supported currencies (used until API data is loaded)
export const DEFAULT_CURRENCIES: Currency[] = [
  { id: 1, code: "USD", name: "US Dollar", symbol: "$", isActive: true, displayOrder: 0 },
  { id: 2, code: "EUR", name: "Euro", symbol: "€", isActive: true, displayOrder: 1 },
  { id: 3, code: "GBP", name: "British Pound", symbol: "£", isActive: true, displayOrder: 2 },
];

// Fetch supported currencies from the API
export async function fetchCurrencies(): Promise<Currency[]> {
  try {
    const currencies = await apiRequest("GET", "/api/currencies");
    
    // Add symbol to each currency
    return currencies.map((currency: Omit<Currency, 'symbol'>) => ({
      ...currency,
      symbol: CURRENCY_SYMBOLS[currency.code] || currency.code
    }));
  } catch (error) {
    console.error("Error fetching currencies:", error);
    return DEFAULT_CURRENCIES;
  }
}

// Add a new currency
export async function addCurrency(code: string, name: string): Promise<Currency | null> {
  try {
    const newCurrency = await apiRequest("POST", "/api/currencies", { code, name });
    return {
      ...newCurrency,
      symbol: CURRENCY_SYMBOLS[newCurrency.code] || newCurrency.code
    };
  } catch (error) {
    console.error("Error adding currency:", error);
    return null;
  }
}

// Toggle currency active status
export async function toggleCurrencyStatus(id: number, isActive: boolean): Promise<Currency | null> {
  try {
    const updatedCurrency = await apiRequest("PATCH", `/api/currencies/${id}`, { isActive });
    return {
      ...updatedCurrency,
      symbol: CURRENCY_SYMBOLS[updatedCurrency.code] || updatedCurrency.code
    };
  } catch (error) {
    console.error("Error updating currency:", error);
    return null;
  }
}

// Get currency details by code
export function getCurrencyByCode(code: string, currencies: Currency[] = DEFAULT_CURRENCIES): Currency {
  return currencies.find(c => c.code === code) || DEFAULT_CURRENCIES[0];
}

// Format an amount with currency symbol
export function formatWithCurrency(amount: number, currencyCode: string = "USD", currencies: Currency[] = DEFAULT_CURRENCIES): string {
  const currency = getCurrencyByCode(currencyCode, currencies);
  
  return `${currency.symbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Convert an amount from one currency to another
export async function convertCurrency(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
  // If the currencies are the same, no conversion needed
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  try {
    // Fetch exchange rate from API
    const data = await apiRequest("GET", `/api/exchange-rates/${fromCurrency}/${toCurrency}`);
    
    return amount * data.rate;
  } catch (error) {
    console.error("Error converting currency:", error);
    // Return the original amount if the conversion fails
    return amount;
  }
}

// Get user's preferred base currency
export async function getUserBaseCurrency(): Promise<string> {
  try {
    const data = await apiRequest("GET", "/api/currency-settings");
    return data.baseCurrency || "USD";
  } catch (error) {
    console.error("Error fetching user currency settings:", error);
    return "USD";
  }
}

// Set user's preferred base currency
export async function setUserBaseCurrency(currencyCode: string): Promise<boolean> {
  try {
    await apiRequest("POST", "/api/currency-settings", {
      baseCurrency: currencyCode
    });
    
    return true;
  } catch (error) {
    console.error("Error setting user currency settings:", error);
    return false;
  }
}

// Format a full amount with currency code
export function formatAmountWithCurrency(amount: number, currencyCode: string = "USD", currencies: Currency[] = DEFAULT_CURRENCIES): string {
  const currency = getCurrencyByCode(currencyCode, currencies);
  
  return `${currency.symbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency.code}`;
}